// src/main.tsx

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
// Platform Foundation design tokens (Central-owned) — defines :root --lu-* CSS
// custom properties consumed by components. First Platform Foundation adoption.
import '@roi-pros/px-tokens/tokens.css';
// Suite Shell CSS from shared package (replaces local suiteShell.css)
import '@levelup/ui-shell/styles.css';
import { GlobalErrorBoundary } from './components/errors/GlobalErrorBoundary';
import { captureError, logFrontendError } from './api/errorTrackingApi';

// Flag to prevent infinite loops in error logging
let isLoggingError = false;

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (isLoggingError) return;
  isLoggingError = true;

  const error = event.reason instanceof Error
    ? event.reason
    : new Error(String(event.reason));

  logFrontendError({
    message: `Unhandled Promise Rejection: ${error.message}`,
    stack: error.stack,
    route: window.location.pathname,
    context: {
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    },
  }).finally(() => {
    isLoggingError = false;
  });
});

// Handle uncaught errors (fallback for any errors not caught by ErrorBoundary)
window.addEventListener('error', (event) => {
  if (isLoggingError) return;
  isLoggingError = true;

  logFrontendError({
    message: `Uncaught Error: ${event.message}`,
    stack: event.error?.stack,
    route: window.location.pathname,
    context: {
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    },
  }).finally(() => {
    isLoggingError = false;
  });
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GlobalErrorBoundary>
      <App />
    </GlobalErrorBoundary>
  </StrictMode>
);
